import { useState } from "react";
import { publications, researchAreas, researchEvidence } from "../data";
import type { ResearchArea } from "../types";
import { researchFigureRegistry } from "./figures";

function getFigureKey(figure: ResearchArea["figure"]) {
  return figure === "peer" ? "peers" : figure;
}

export function ResearchFigure({
  area,
  compact = false
}: {
  area: ResearchArea;
  compact?: boolean;
}) {
  const evidence = researchEvidence.find((item) => item.id === area.figure);
  const Figure = researchFigureRegistry[getFigureKey(area.figure)];
  const publication = publications.find((item) => item.id === area.publicationId);

  return (
    <Figure
      className={compact ? "diagram diagram-compact" : "diagram"}
      title={evidence?.title}
      description={
        evidence
          ? `${evidence.facts.map((fact) => `${fact.label}: ${fact.value}`).join(". ")}.`
          : area.summary
      }
      caption={
        compact ? undefined : (
          <span>
            {publication?.authors ? `${publication.authors}. ` : ""}
            <i>{evidence?.journal || publication?.venue}</i>, {evidence?.year || publication?.year}.
            {publication?.doi ? ` DOI ${publication.doi}.` : ""} {evidence?.reuseNote}
          </span>
        )
      }
    />
  );
}

export function ResearchExplorer() {
  const [selected, setSelected] = useState(0);
  const area = researchAreas[selected];
  const publication = publications.find((item) => item.id === area.publicationId);

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const direction = event.key === "ArrowDown" ? 1 : -1;
    setSelected((current) => (current + direction + researchAreas.length) % researchAreas.length);
  }

  return (
    <div className="research-explorer">
      <div
        className="research-tabs"
        role="tablist"
        aria-label="선정 연구"
        aria-orientation="vertical"
        onKeyDown={onKeyDown}
      >
        {researchAreas.map((item, index) => (
          <button
            type="button"
            role="tab"
            aria-selected={selected === index}
            aria-controls={`research-panel-${item.id}`}
            id={`research-tab-${item.id}`}
            tabIndex={selected === index ? 0 : -1}
            className={selected === index ? "research-tab is-active" : "research-tab"}
            key={item.id}
            onClick={() => setSelected(index)}
          >
            <span className="research-tab-index">{String(index + 1).padStart(2, "0")}</span>
            <span className="research-tab-title">{item.title}</span>
          </button>
        ))}
      </div>
      <div
        className="research-stage"
        role="tabpanel"
        id={`research-panel-${area.id}`}
        aria-labelledby={`research-tab-${area.id}`}
      >
        <div className="research-figure" key={area.id}>
          <ResearchFigure area={area} compact />
        </div>
        <div className="research-stage-copy">
          <div>
            <h3>{area.title}</h3>
            <p lang="en">{publication?.title}</p>
          </div>
          <p className="research-method">
            <strong>DATA · METHOD</strong>
            {area.method}
          </p>
        </div>
      </div>
    </div>
  );
}
